import { FC, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { checkAHAVI, replaceHtml } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { getCxlClass } from 'utils/hotel';
import { dateFormat } from 'utils/shared/utils';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

interface BookCancellationProps {
  book: BookResponse;
  docket: string;
  setDocket: (value: string) => void;
  tourSubject: string;
  setTourSubject: (value: string) => void;
  isSubmitted: boolean;
}

const BookCancellation: FC<BookCancellationProps> = ({
  book,
  docket,
  setDocket,
  tourSubject,
  setTourSubject,
  isSubmitted,
}) => {
  const [t] = useTranslation();
  const { bookConfig } = useContext(MainContext);
  const cxlExpired = new Date(book.roomClass.cxl.limitDate) < new Date();
  const cancellationPolicy = replaceHtml(book.cancellationPolicy.desc);
  const cxlClass = getCxlClass(book.roomClass.cxl.limitDate);

  return (
    <div className="book-form default-shadow flex-column book">
      <div className="title-underline title-header-grey text-lg mb-5">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-5" size={'sm'} />
        {t('passenger.cancellation_details')}
      </div>
      <div className={cxlClass + ' mb-10 flex-one'} style={{ marginTop: 0, maxHeight: 'unset' }}>
        {t('passenger.cancellation_date')}:{' '}
        <span className="text-bold">
          {dateFormat(book.cancellationPolicy.date, 'EEE - dd MMMM, yyyy')}
        </span>
        {b2b && (
          <div
            className={checkAHAVI(cancellationPolicy) ? 'rtl' : 'ltr'}
            dangerouslySetInnerHTML={{ __html: cancellationPolicy }}
          ></div>
        )}
      </div>
      {!!book.roomClass.remarks && (
        <div
          className="block-wr block-mh block-warning mb-5 flex-one"
          style={{ maxHeight: 'unset', marginTop: 0 }}
        >
          <div className="title text-st mb-5">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-5" />
            {t('passenger.important_notes')}
          </div>
          <div className="">{book.roomClass.remarks}</div>
        </div>
      )}
      <div className="row">
        {bookConfig.docket.display === '1' && (
          <FormGroup
            onChange={setDocket}
            value={docket}
            label={t('mo.docket')}
            className="col-md-4 col-12"
            validation={{ required: bookConfig.docket.mandatory === '1' }}
            isSubmitted={isSubmitted}
          />
        )}
        {bookConfig.tourSubject.display === '1' && (
          <FormGroup
            onChange={setTourSubject}
            value={tourSubject}
            label={t('mo.tour_subject')}
            className="col-md-4 col-12"
            validation={{ required: bookConfig.tourSubject.mandatory === '1' }}
            isSubmitted={isSubmitted}
          />
        )}
      </div>
      <div className="title-footer-grey text-lg text-bold" style={{ marginTop: 'auto' }}>
        {cxlExpired && (
          <span className="color-danger">{t('hotel.reservation_is_not_refundable')}</span>
        )}
        {!cxlExpired && (
          <span className="text-success">{t('hotel.reservation_is_refundable')}</span>
        )}
      </div>
    </div>
  );
};

export default BookCancellation;
